import type { Address } from "viem"
import type { Config } from "wagmi"
import { action, computed, makeObservable, observable, override, runInAction } from "mobx"

import type { EmitEvent } from "@/domains/core"
import type { IEventBus } from "@/domains/events"
import type { IWalletConnectionStore } from "@/domains/wallet/types"
import type { EventEmitter } from "@/domains/library"

import { THICK_V1_BASE_ADDRESSES } from "../constants"
import { collectFeesFromThickNfpInteraction } from "../interactions"
import type { ManualClPositionRepository } from "../positions/manual-cl-position-repository"
import type { ManualClPositionStore } from "../positions/manual-cl-position-store"
import { humanizeTransactionError } from "../utils/humanize-transaction-error"
import { ManagePositionStoreBase } from "./manage-position-store-base"

/**
 * Manage-position store for the "collect fees" flow.
 *
 * The NFPM's `collect` takes no real decision parameters — the user just opts
 * in to receive whatever fees have accrued. This store therefore surfaces
 * nothing but display + submission state:
 *
 *   - `pendingFees0` / `pendingFees1` — read straight from the position's
 *     last on-chain simulated collect (refreshed by the orchestrator).
 *   - `hasClaimableFees` computed gates the submit button.
 *
 * On success it publishes `ManualClFeesCollectedEvent`, which the position
 * orchestrator subscribes to and uses to force-refresh both the GraphQL and
 * on-chain queries so the "claimable" display drops to zero.
 */
export class CollectFeesStore extends ManagePositionStoreBase {
  isSubmitting: boolean = false
  submissionError: string | undefined = undefined

  constructor(
    positionRepository: ManualClPositionRepository,
    private readonly chainId: number,
    private readonly config: Config,
    private readonly eventBus: IEventBus,
    private readonly emitter: EventEmitter<EmitEvent>,
    private readonly walletStore: IWalletConnectionStore,
  ) {
    super(positionRepository)
    makeObservable(this, {
      isSubmitting: observable,
      submissionError: observable,
      submit: action,
      reset: override,
      pendingFees0: computed,
      pendingFees1: computed,
      hasClaimableFees: computed,
      canSubmit: computed,
    })
  }

  private get activePosition(): ManualClPositionStore | undefined {
    return this.position
  }

  get pendingFees0(): bigint | undefined {
    return this.activePosition?.pendingFees0
  }

  get pendingFees1(): bigint | undefined {
    return this.activePosition?.pendingFees1
  }

  get hasClaimableFees(): boolean {
    const f0 = this.pendingFees0 ?? 0n
    const f1 = this.pendingFees1 ?? 0n
    return f0 > 0n || f1 > 0n
  }

  get canSubmit(): boolean {
    if (!this.activePosition) return false
    if (this.isSubmitting) return false
    if (!this.walletStore.isConnected) return false
    if (this.activeNfpmTokenId === undefined) return false
    return this.hasClaimableFees
  }

  async submit(): Promise<void> {
    const position = this.activePosition
    if (!position) return
    const nfpmTokenId = this.activeNfpmTokenId
    if (nfpmTokenId === undefined) {
      runInAction(() => {
        this.submissionError = "NFP ID is still resolving. Please try again in a moment."
      })
      return
    }
    const account = this.walletStore.connectedAddress?.raw
    if (!account) return

    runInAction(() => {
      this.isSubmitting = true
      this.submissionError = undefined
    })

    try {
      const result = await collectFeesFromThickNfpInteraction({
        account,
        chainId: this.chainId,
        config: this.config,
        tokenId: nfpmTokenId,
        poolAddress: position.pool.address.raw as Address,
        nfpManagerAddress: THICK_V1_BASE_ADDRESSES.nfPositionManager,
        emitter: this.emitter,
        eventBus: this.eventBus,
      })

      if (result.success === false) {
        const humanized = humanizeTransactionError(result.error)
        if (humanized !== null) {
          runInAction(() => {
            this.submissionError = humanized
          })
        }
      }
    } catch (error) {
      const humanized = humanizeTransactionError(error)
      if (humanized !== null) {
        runInAction(() => {
          this.submissionError = humanized
        })
      }
    } finally {
      runInAction(() => {
        this.isSubmitting = false
      })
    }
  }

  reset(): void {
    super.reset()
    this.isSubmitting = false
    this.submissionError = undefined
  }
}
